import { parse, extract } from 'query-string';
import { is, prop, compose, when } from 'ramda';

import { COMMON } from 'translations';

import { query } from '../../api/prismic';
import { compact, path } from '../../helpers/compact';
import error, { getErrorObject } from '../../helpers/error';
import { LOCALES, toPrismicLang } from '../../helpers/i18n';
import { getPager } from '../../selectors/paginations';

export const LOAD_PAGINATED_QUERY_REQUEST = 'LOAD_PAGINATED_QUERY_REQUEST';
export const LOAD_PAGINATED_QUERY_SUCCESS = 'LOAD_PAGINATED_QUERY_SUCCESS';
export const LOAD_PAGINATED_QUERY_FAILURE = 'LOAD_PAGINATED_QUERY_FAILURE';
export const PAGINATION_RESET = 'PAGINATION_RESET';

export const loadPaginatedQueryRequest = (lang, key, opts) => ({
  type: LOAD_PAGINATED_QUERY_REQUEST,
  payload: opts,
  lang,
  key,
});

export const loadPaginatedQuerySuccess = (
  lang,
  key,
  result,
  end = false,
  context = {}
) => ({
  type: LOAD_PAGINATED_QUERY_SUCCESS,
  payload: result,
  context,
  end,
  lang,
  key,
});

export const loadPaginatedQueryFailure = (lang, key, err) => ({
  type: LOAD_PAGINATED_QUERY_FAILURE,
  payload: getErrorObject(err, COMMON.ERROR_LOADING_MORE, { key }),
  lang,
  key,
});

export const reset = (lang, key) => ({
  type: PAGINATION_RESET,
  key,
  lang,
});

export const resetPagers = (...pagers) => (
  dispatch,
  getState // eslint-disable-line
) => LOCALES.map((l) => pagers.map((p) => dispatch(reset(l, p))));

const options = { page: 1, pageSize: 20 };

const hasReachedEnd = (result) => result.page >= result.total_pages;

const extractPage = when(
  Boolean,
  compose(Number, prop('page'), parse, extract)
);

const getPaginatedDocuments = async (pager, filters, opts) => {
  const next = path('context.next', pager);
  if (next) {
    return await query(filters, { ...opts, page: next });
  }
  return await query(filters, opts);
};

const isValidResultResponse = (res) =>
  is(Number, prop('page', res)) &&
  is(Number, prop('total_pages', res)) &&
  is(Array, prop('results', res));

/**
 * A complete paginated query workflow. It heavily relies upon the current state to
 * use the best query method. What it actually does is looking for the pager state for the
 * given query key, if a next page exist on the context then it uses it, otherwise it just
 * queries Prismic with the given filters & options.
 *
 * @param {string} key     A uniq key for the query
 * @param {any[]}  filters A list of prismic predicates
 * @param {object} opts    Query options
 *
 * @see https://prismic.io/docs/javascript/query-the-api/query-predicates-reference
 * @see https://prismic.io/docs/javascript/query-the-api/query-options-reference
 */
export const getDocuments = (key, filters = [], opts = options) => async (
  dispatch,
  getState
) => {
  const state = getState();
  const locale = state.locale;
  const lang = toPrismicLang(locale);
  const pager = getPager(key, state);
  const options = { page: opts.page, count: opts.pageSize, lang };

  try {
    dispatch(loadPaginatedQueryRequest(locale, key, options));

    const result = await getPaginatedDocuments(pager, filters, {
      ...opts,
      lang,
    });

    if (!isValidResultResponse(result)) {
      throw error(
        '400',
        `There was an error during the query for ${key}.`,
        'Query error',
        { filters, opts, result }
      );
    }

    const end = hasReachedEnd(result);
    const context = compact({
      total: prop('total_results_size', result),
      next: extractPage(prop('next_page', result)),
      prev: extractPage(prop('prev_page', result)),
      page: prop('page', result),
    });
    dispatch(
      loadPaginatedQuerySuccess(
        locale,
        key,
        { result: result.results, entities: result.entities },
        end,
        context
      )
    );
  } catch (error) {
    dispatch(loadPaginatedQueryFailure(locale, key, error));
  }
};
